/**
 * The candidate demographic fields a driver's-license barcode scan can pre-fill (`BACKLOG.md` C19,
 * `docs/decisions/patient-license-scan-scope.md` §5). A deliberately narrow subset of
 * `clinicalRequests.ts`'s own `updatePatientRequestSchema` fields — exactly the ones an AAMVA DL/ID
 * barcode's tagged fields can supply (`licenseScanParser.ts`'s own `TAG_FIELD_MAP`) — reusing that
 * file's field names, bounds, and value sets rather than inventing a second vocabulary for the same
 * demographic concepts.
 *
 * No license number, class, or expiration field here, by design — those AAMVA subfields exist on a
 * real card but `patients` has no matching column and this feature has no reason to invent one
 * (`docs/decisions/patient-license-scan-scope.md` §5's own minimum-necessary-data argument).
 */

import { z } from 'zod';

import {
  administrativeSexSchema,
  calendarDateSchema,
  MAX_ADDRESS_LINE_LENGTH,
  MAX_CITY_LENGTH,
  MAX_NAME_LENGTH,
  MAX_POSTAL_CODE_LENGTH,
  MAX_STATE_LENGTH,
} from './clinicalRequests.ts';

/** Every field a scan can propose a value for — also the closed vocabulary
 * `resolveLicenseScanRequestSchema`'s `acceptedFields` draws from (`licenseScan.ts`), so a staff
 * accept/keep decision can only ever name a field this schema actually has. */
export const LICENSE_SCAN_FIELD_KEYS = [
  'firstName',
  'lastName',
  'dateOfBirth',
  'administrativeSex',
  'addressLine1',
  'city',
  'state',
  'postalCode',
] as const;
export type LicenseScanFieldKey = (typeof LICENSE_SCAN_FIELD_KEYS)[number];
export const licenseScanFieldKeySchema = z.enum(LICENSE_SCAN_FIELD_KEYS);

/**
 * Every field optional — a real scan rarely yields all eight (a damaged barcode region, a card
 * predating a given subfield, a jurisdiction that omits one), and `licenseScanParser.ts`'s own
 * fail-closed-per-field posture means a partial result is the expected shape, not an error case.
 * This is both the client's request-body shape for `POST /api/license-scans/capture` (nested under
 * `captureLicenseScanRequestSchema`, `licenseScan.ts`) and the stored shape of
 * `patient_license_scans.extracted_fields` — the same jsonb blob a capture writes is what a staff
 * review later reads back, so one schema validates both ends.
 */
export const licenseScanExtractedFieldsSchema = z.strictObject({
  firstName: z.string().trim().min(1).max(MAX_NAME_LENGTH).optional(),
  lastName: z.string().trim().min(1).max(MAX_NAME_LENGTH).optional(),
  dateOfBirth: calendarDateSchema.optional(),
  administrativeSex: administrativeSexSchema.optional(),
  addressLine1: z.string().trim().min(1).max(MAX_ADDRESS_LINE_LENGTH).optional(),
  city: z.string().trim().min(1).max(MAX_CITY_LENGTH).optional(),
  state: z.string().trim().min(1).max(MAX_STATE_LENGTH).optional(),
  postalCode: z.string().trim().min(1).max(MAX_POSTAL_CODE_LENGTH).optional(),
});
export type LicenseScanExtractedFields = z.infer<typeof licenseScanExtractedFieldsSchema>;
